import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { BsGear, BsSliders, BsBarChart } from 'react-icons/bs';
import Logo from '../Logo';
import ThemeToggle from '../../theme/ThemeToggle';
import ChatToggleIcon from '../ai/ChatToggleIcon';
import SplitLayout from '../ai/SplitLayout';
import ChatPanel from '../ai/ChatPanel';
import ChatOverlay from '../ai/ChatOverlay';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import * as lucaAgentService from '../../services/lucaAgentService';
import FeatureSetTiles from './FeatureSetTiles';
import Configurations from './Configurations';
import Visualization from './Visualization';
import NavUserProfile from './NavUserProfile';
import './AppLayout.css';

const NEW_CHAT_ID = 'new';
const newChatEntry = {
  id: NEW_CHAT_ID,
  title: 'New chat',
  messages: [],
  backendConversationId: null,
};

function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, getSessionIdToken, user } = useAuth();
  const { toast } = useToast();
  const isStreamingRef = useRef(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatPanelWidth, setChatPanelWidth] = useState(30);
  const [conversations, setConversations] = useState([newChatEntry]);
  const [activeConversationId, setActiveConversationId] = useState(NEW_CHAT_ID);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(function () {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;
  });

  useEffect(
    function () {
      const mql = window.matchMedia('(max-width: 1024px)');
      function onChange() {
        setIsMobile(mql.matches);
      }
      mql.addEventListener('change', onChange);
      return function () {
        mql.removeEventListener('change', onChange);
      };
    },
    []
  );

  // Load past chats from API when chat panel opens and user is signed in
  useEffect(
    function () {
      if (!chatOpen) return;
      getSessionIdToken()
        .then(function (token) {
          if (!token) {
            setConversations([newChatEntry]);
            setConversationsLoaded(true);
            return;
          }
          return lucaAgentService.getConversations(token).then(function (list) {
            const fromApi = (list || []).map(function (c) {
              return {
                id: c.id,
                title: c.title || 'Chat',
                messages: [],
                backendConversationId: c.id,
              };
            });
            setConversations(function (prev) {
              const hasNew = prev.some(function (c) {
                return c.id === NEW_CHAT_ID;
              });
              return hasNew ? [newChatEntry, ...fromApi] : fromApi;
            });
            setConversationsLoaded(true);
          });
        })
        .catch(function () {
          setConversations([newChatEntry]);
          setConversationsLoaded(true);
        });
    },
    [chatOpen, getSessionIdToken]
  );

  // When user selects a conversation that has no messages loaded, fetch it
  function handleSelectConversation(id) {
    setActiveConversationId(id);
    if (id === NEW_CHAT_ID) return;
    const conv = conversations.find(function (c) {
      return c.id === id;
    });
    if (!conv || conv.messages.length > 0) return;
    getSessionIdToken()
      .then(function (token) {
        if (!token) return;
        return lucaAgentService.getConversation(id, token).then(function (data) {
          const messages = (data.messages || []).map(function (m) {
            return { sender: m.sender || 'user', text: m.text || '' };
          });
          setConversations(function (prev) {
            return prev.map(function (c) {
              if (c.id !== id) return c;
              return { ...c, messages, title: data.title || c.title };
            });
          });
        });
      })
      .catch(function () {
        toast('Failed to load conversation.', 'error');
      });
  }

  const isMainPage = location.pathname === '/app' || location.pathname === '/app/';
  const isConfigurations = location.pathname === '/app/configurations';
  const isVisualization = location.pathname === '/app/visualization';

  function handleSignOut() {
    logout();
    toast('Signed out successfully.', 'info');
    navigate('/signin');
  }

  const scrollbarGap = 10; /* reserved at viewport right for main scrollbar */

  function handleResizeStart(e) {
    e.preventDefault();
    function onMouseMove(e2) {
      const rightPanelWidth = window.innerWidth - e2.clientX - scrollbarGap;
      const pct = (rightPanelWidth / window.innerWidth) * 100;
      setChatPanelWidth(function (prev) {
        const next = Math.min(50, Math.max(20, pct));
        return next;
      });
    }
    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  async function handleSendMessage(text) {
    if (isStreamingRef.current) {
      toast('Please wait for the current response to finish.', 'info');
      return;
    }
    let idToken = null;
    try {
      idToken = await getSessionIdToken();
    } catch (_) {}
    if (!idToken) {
      toast('Please sign in to use chat.', 'error');
      return;
    }
    const currentConvId = activeConversationId;
    setConversations(function (prev) {
      return prev.map(function (conv) {
        if (conv.id !== currentConvId) return conv;
        return {
          ...conv,
          messages: [
            ...conv.messages,
            { sender: 'user', text },
            { sender: 'agent', text: '' },
          ],
        };
      });
    });
    const activeConv = conversations.find(function (c) {
      return c.id === currentConvId;
    });
    const sessionId =
      (activeConv && activeConv.backendConversationId) || lucaAgentService.initSession();
    const userId = (user && (user.email || user.username || user.sub)) || null;
    isStreamingRef.current = true;
    lucaAgentService
      .sendMessage(
        { message: text, sessionId, userId, idToken },
        {
          onChunk: function (chunk) {
            setConversations(function (prev) {
              return prev.map(function (conv) {
                if (conv.id !== currentConvId) return conv;
                const messages = conv.messages.slice();
                const lastIdx = messages.length - 1;
                if (lastIdx >= 0 && messages[lastIdx].sender === 'agent') {
                  messages[lastIdx] = { ...messages[lastIdx], text: messages[lastIdx].text + chunk };
                }
                return { ...conv, messages };
              });
            });
          },
        }
      )
      .then(function ({ text: replyText, raw }) {
        isStreamingRef.current = false;
        const newSessionId = raw.sessionId != null ? raw.sessionId : sessionId;
        setConversations(function (prev) {
          return prev.map(function (conv) {
            if (conv.id !== currentConvId) return conv;
            const messages = conv.messages.slice();
            const lastIdx = messages.length - 1;
            if (lastIdx >= 0 && messages[lastIdx].sender === 'agent') {
              messages[lastIdx] = { ...messages[lastIdx], text: replyText };
            }
            const updated = { ...conv, messages, backendConversationId: newSessionId };
            if (conv.id === NEW_CHAT_ID && newSessionId) {
              updated.id = newSessionId;
              updated.title = conv.title || 'New chat';
            }
            return updated;
          });
        });
        if (currentConvId === NEW_CHAT_ID && newSessionId) {
          setActiveConversationId(newSessionId);
        }
      })
      .catch(function (err) {
        isStreamingRef.current = false;
        const status = err && err.status;
        const detail = (err && err.detail) || (err && err.message);
        if (status === 401) {
          toast('Please sign in again.', 'error');
          setConversations(function (prev) {
            return prev.map(function (conv) {
              if (conv.id !== currentConvId) return conv;
              const messages = conv.messages.slice(0, -2);
              return { ...conv, messages };
            });
          });
          navigate('/signin');
        } else if (status === 403) {
          toast(detail || 'Access denied', 'error');
        } else {
          toast(detail || lucaAgentService.UNAVAILABLE_MESSAGE, 'error');
        }
        const fallbackMessage =
          status === 401
            ? 'Please sign in again.'
            : status === 403
              ? (detail || 'Access denied')
              : lucaAgentService.UNAVAILABLE_MESSAGE;
        setConversations(function (prev) {
          return prev.map(function (conv) {
            if (conv.id !== currentConvId) return conv;
            const messages = conv.messages.slice();
            const lastIdx = messages.length - 1;
            if (lastIdx >= 0 && messages[lastIdx].sender === 'agent') {
              messages[lastIdx] = { ...messages[lastIdx], text: fallbackMessage };
            }
            return { ...conv, messages };
          });
        });
      });
  }

  const appBody = (
      <div className="app-body">
        <nav className="app-nav">
          <div className="app-nav-items">
            <button
              type="button"
              className={'app-nav-item' + (isMainPage && !chatOpen ? ' app-nav-item--active' : '')}
              onClick={function () {
                setChatOpen(false);
                navigate('/app');
              }}
            >
              <span className="app-nav-icon">
                <BsGear size={18} />
              </span>
              <span>Channels</span>
            </button>
            <button
              type="button"
              className={'app-nav-item' + (isConfigurations && !chatOpen ? ' app-nav-item--active' : '')}
              onClick={function () {
                setChatOpen(false);
                navigate('/app/configurations');
              }}
            >
              <span className="app-nav-icon">
                <BsSliders size={18} />
              </span>
              <span>Configurations</span>
            </button>
            <button
              type="button"
              className={'app-nav-item' + (isVisualization && !chatOpen ? ' app-nav-item--active' : '')}
              onClick={function () {
                setChatOpen(false);
                navigate('/app/visualization');
              }}
            >
              <span className="app-nav-icon">
                <BsBarChart size={18} />
              </span>
              <span>Visualization</span>
            </button>
          </div>
          <NavUserProfile />
        </nav>

        <div className="app-main-scroll">
          <main className={'app-main' + (isVisualization ? ' app-main--visualization' : '')}>
            {isMainPage ? (
            <FeatureSetTiles />
          ) : isConfigurations ? (
            <Configurations />
          ) : isVisualization ? (
            <Visualization />
          ) : (
            <Outlet />
          )}
          </main>
        </div>
      </div>
  );

  return (
    <div className="app-layout">
      <header className="app-header">
        <Link to="/app" className="app-logo-link">
          <Logo size="small" />
        </Link>
        <div className="app-header-right">
          <ThemeToggle />
          <ChatToggleIcon
            isOpen={chatOpen}
            onClick={function () {
              setChatOpen(function (prev) {
                return !prev;
              });
            }}
          />
          <button type="button" className="app-signout" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <div
        className={'app-scroll-container' + (isVisualization ? ' app-scroll-container--visualization' : '')}
        style={
          chatOpen && !isMobile
            ? { marginRight: `calc(${scrollbarGap}px + 6px + ${chatPanelWidth}%)` }
            : undefined
        }
      >
        {chatOpen && isMobile ? (
          <ChatOverlay>
            <ChatPanel
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelectConversation={handleSelectConversation}
              onSendMessage={handleSendMessage}
              onClose={function () {
                setChatOpen(false);
              }}
            />
          </ChatOverlay>
        ) : chatOpen ? (
          <SplitLayout
            panelWidthPercent={chatPanelWidth}
            onResize={handleResizeStart}
            rightContent={
              <ChatPanel
                conversations={conversations}
                activeConversationId={activeConversationId}
                onSelectConversation={handleSelectConversation}
                onSendMessage={handleSendMessage}
                onClose={function () {
                  setChatOpen(false);
                }}
              />
            }
          >
            {appBody}
          </SplitLayout>
        ) : (
          appBody
        )}
      </div>
    </div>
  );
}

export default AppLayout;
